/**
 * Severity simulator (SPEC_Kontext_Portal §3).
 *
 * Not decoration: this is the review tool for the rule set. An expert cannot
 * judge a rule table, but can judge "lift for a hospital, public area → 100".
 * Without it the release is a signature on something unreadable.
 *
 * Everything shown comes from the server's `resolveSi` — the same function the
 * pipeline uses. Nothing is recomputed here, deliberately: a rebuilt simulation
 * drifts and eventually simulates something other than what the system does,
 * which is worse than none because someone released against it.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { damageTypeLabel } from "./hazardDisplay";
import ContextMatrix from "./ContextMatrix";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

interface Properties {
  refreshSignal: number;
}

/**
 * GEFMA anchors from the data spec. Showing the expected value next to the
 * result puts the regression tests in front of the reviewer instead of leaving
 * them in a test script.
 *
 * `usageClass` values are the codes of `Legal.UsageClass` — `hospital`,
 * `office`, `garage`. They are deliberately not the German snake_case of the
 * spec sheet: the vocabulary collection is the authority here and carries the
 * English codes, verified against the database. Changing them to
 * `krankenhaus_pflege` / `buero_verwaltung` silently breaks the anchors,
 * because no rule then matches and the drinking-water case returns 90.
 */
const PRESETS = [
  {
    key: "coGarage",
    anchor: true,
    regulatedObjectCode: "coWarningSystem",
    hazardCode: "P11",
    usageClass: "garage",
    personExposure: "technikbereich",
    expected: 90 as number | null,
  },
  {
    key: "drinkingWaterHospital",
    anchor: true,
    regulatedObjectCode: "drinkingWaterInstallation",
    hazardCode: "P12",
    usageClass: "hospital",
    personExposure: null,
    expected: 100,
  },
  {
    key: "fireAlarm",
    anchor: true,
    regulatedObjectCode: "fireAlarmSystem",
    hazardCode: "P10",
    usageClass: "office",
    personExposure: "technikbereich",
    expected: 100,
  },
  /**
   * Not an anchor — a demonstration. All three anchors sit on a base of 100,
   * which is the top rung: context can only ever saturate there, so the bar
   * never moves and the tool looks inert on arrival. This case has a base of 90,
   * so switching between public area (R1 lifts to 100) and plant room (R1 and R3
   * cancel, stays 90) actually shows the mechanism the panel exists to explain.
   */
  {
    key: "elevatorHospital",
    anchor: false,
    regulatedObjectCode: "elevatorSystem",
    hazardCode: "P01",
    usageClass: "hospital",
    personExposure: "publikumsbereich",
    expected: null,
  },
];

/** „Nicht erfasst“ ist ein echter Zustand — Radix erlaubt keinen leeren Wert. */
const NOT_RECORDED = "_notRecorded";

/**
 * The band bar — the signature element of this view. It shows at a glance that
 * the scale is discrete and that context moves exactly one step. A number alone
 * does not convey that.
 *
 * The transitions are the argument, not decoration: the marker travels the one
 * rung it is allowed to travel, and the connector grows to meet it. Motion stops
 * at a rung, never between two — the scale has no values in between, and an
 * easing that drifted past one would say otherwise. Suppressed entirely for
 * anyone who asked for reduced motion.
 *
 * Die Farben sind Datenvisualisierung (Basis vs. Ergebnis) und bleiben
 * wertgleich; nur die unbeteiligten Sprossen laufen jetzt über das
 * muted-foreground-Token — #999 auf Weiß lag bei 2.85:1.
 */
const MOTION = "@media (prefers-reduced-motion: reduce)";
const BandBar = ({
  band,
  kappa,
  token,
  t,
}: {
  band: { values: number[]; baseIndex: number | null; resultIndex: number | null };
  kappa: number | null | undefined;
  token: number;
  t: (key: string) => string;
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<HTMLDivElement | null>(null);
  const rungRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [marker, setMarker] = useState<{ left: number; width: number } | null>(null);

  const values = band?.values ?? [];
  const baseIndex = band?.baseIndex ?? null;
  const resultIndex = band?.resultIndex ?? null;
  const valueKey = values.join(",");
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.(MOTION.replace("@media ", ""))?.matches;

  // Measured from the real rung geometry, not from an assumed stride: "100" is
  // wider than "5", so a fixed step width would let the marker drift off centre.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const rung = resultIndex === null ? null : rungRefs.current[resultIndex];
    if (!track || !rung) {
      setMarker(null);
      return;
    }
    const trackBox = track.getBoundingClientRect();
    const rungBox = rung.getBoundingClientRect();
    setMarker({ left: rungBox.left - trackBox.left, width: rungBox.width });
  }, [resultIndex, valueKey]);

  /**
   * A shift the band cannot absorb. Without this the panel says "context: +1"
   * next to an unchanged result and looks broken — which is exactly how the
   * three GEFMA anchors present, since all of them sit on a base of 100.
   */
  const desired = baseIndex !== null && kappa ? baseIndex + kappa : null;
  const saturatedUp = desired !== null && desired > values.length - 1;
  const saturatedDown = desired !== null && desired < 0;
  const saturated = saturatedUp || saturatedDown;

  // The marker runs up against the edge and is pushed back — the same gesture a
  // physical slider makes at its stop.
  useEffect(() => {
    const node = markerRef.current;
    if (!node || !saturated || reduceMotion || !marker) return;
    const base = `translateX(${marker.left}px)`;
    const nudge = saturatedUp ? 7 : -7;
    node.animate(
      [
        { transform: base },
        { transform: `translateX(${marker.left + nudge}px)`, offset: 0.45 },
        { transform: base },
      ],
      { duration: 420, easing: "cubic-bezier(0.34, 1.56, 0.64, 1)" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, saturated, saturatedUp, marker?.left]);

  if (!values.length) return null;

  return (
    <div className="min-w-0 overflow-x-auto">
      <div ref={trackRef} className="relative flex items-center">
        {/* The one thing on the bar that actually moves. Colour alone reads as a
            jump — a body that slides the single rung shows which way context
            pushed and how far, even at the edge of vision. */}
        {marker ? (
          <div
            ref={markerRef}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              marginTop: "-13px",
              height: "26px",
              width: `${marker.width}px`,
              borderRadius: "13px",
              backgroundColor: "#004578",
              transform: `translateX(${marker.left}px)`,
              transition: reduceMotion
                ? undefined
                : "transform 420ms cubic-bezier(0.22, 1, 0.36, 1), width 420ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        ) : null}
        {values.map((value, index) => {
          const isBase = index === baseIndex;
          const isResult = index === resultIndex;
          const between =
            baseIndex !== null &&
            resultIndex !== null &&
            index > Math.min(baseIndex, resultIndex) &&
            index <= Math.max(baseIndex, resultIndex);
          return (
            <span key={value} className="relative z-[1] flex items-center">
              {index > 0 ? (
                <span
                  style={{
                    width: "44px",
                    height: between ? "3px" : "1px",
                    backgroundColor: between ? "#004578" : "#ddd",
                    transition: reduceMotion
                      ? undefined
                      : "height 260ms ease, background-color 260ms ease",
                  }}
                />
              ) : null}
              <span
                ref={(node) => {
                  rungRefs.current[index] = node;
                }}
                style={{
                  minWidth: "40px",
                  textAlign: "center",
                  padding: "3px 8px",
                  borderRadius: "12px",
                  fontWeight: isBase || isResult ? 700 : 400,
                  color: isResult
                    ? "#fff"
                    : isBase
                      ? "#004578"
                      : "hsl(var(--muted-foreground))",
                  // The result rung stays transparent — the travelling marker is
                  // its body. Only the base keeps a fill of its own.
                  backgroundColor: isBase && !isResult ? "#deecf9" : "transparent",
                  border: isBase && !isResult ? "1px solid #004578" : "1px solid transparent",
                  // Held back so the digits turn white as the marker arrives
                  // under them, not before it has left.
                  transition: reduceMotion
                    ? undefined
                    : "color 200ms ease 160ms, background-color 260ms ease, border-color 260ms ease",
                }}
              >
                {value}
              </span>
            </span>
          );
        })}
      </div>
      <div className="flex text-xs text-muted-foreground">
        {values.map((value, index) => (
          <span key={value} className="flex">
            {index > 0 ? <span style={{ width: "44px" }} /> : null}
            <span style={{ minWidth: "40px", textAlign: "center" }}>
              {index === band.baseIndex ? t("context.simulator.base") : ""}
              {index === band.resultIndex && index !== band.baseIndex
                ? t("context.simulator.result")
                : ""}
            </span>
          </span>
        ))}
      </div>
      {saturated ? (
        <p className="mt-1 min-w-0 break-words text-xs text-warning">
          {t(
            saturatedUp
              ? "context.simulator.saturatedUp"
              : "context.simulator.saturatedDown",
          )}
        </p>
      ) : null}
    </div>
  );
};

export default function SeveritySimulatorPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [options, setOptions] = useState<any>(null);
  const [typeCode, setTypeCode] = useState<string | undefined>(undefined);
  const [hazardCode, setHazardCode] = useState<string | undefined>(undefined);
  const [usageClass, setUsageClass] = useState<string | undefined>(undefined);
  const [personExposure, setPersonExposure] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<any>(null);
  const [resultToken, setResultToken] = useState(0);
  const [expected, setExpected] = useState<number | null>(null);
  const [showTrace, setShowTrace] = useState(false);
  const [matrix, setMatrix] = useState<any>(null);
  const [sweep, setSweep] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.getSimulatorOptions();
        if (!cancelled) setOptions(result);
      } catch (error) {
        if (!cancelled) pushError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const types: any[] = options?.types || [];
  const selectedType = useMemo(
    () => types.find((type: any) => type.code === typeCode),
    [types, typeCode],
  );

  // The hazard picker is filtered to H(RT): pick a type and you see only its
  // hazards, which conveys the model structure in passing.
  const hazardOptions = selectedType?.hazards || [];

  useEffect(() => {
    if (!typeCode || !hazardCode) {
      setResult(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        const simulation = await legalAPI.simulateSeverity({
          regulatedObjectCode: typeCode,
          hazardCode,
          usageClass: usageClass ?? null,
          personExposure: personExposure ?? null,
        });
        if (!cancelled) {
          setResult(simulation);
          // Every answered simulation gets its own token, so the marker bumps
          // against the band edge again even when two cells give the same value.
          setResultToken((value) => value + 1);
        }
      } catch (error) {
        if (!cancelled) pushError(error);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeCode, hazardCode, usageClass, personExposure]);

  useEffect(() => {
    if (!typeCode || !hazardCode) {
      setMatrix(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.getContextMatrix(typeCode, hazardCode);
        if (!cancelled) setMatrix(result);
      } catch (error) {
        if (!cancelled) pushError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeCode, hazardCode]);

  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    setTypeCode(preset.regulatedObjectCode);
    setHazardCode(preset.hazardCode);
    setUsageClass(preset.usageClass ?? undefined);
    setPersonExposure(preset.personExposure ?? undefined);
    setExpected(preset.expected);
  };

  const runSweep = async () => {
    try {
      setSweepBusy(true);
      setSweep(await legalAPI.getContextSweep());
    } catch (error) {
      pushError(error);
    } finally {
      setSweepBusy(false);
    }
  };

  const deviates = expected !== null && result?.siValue !== null && result?.siValue !== expected;

  if (!options) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-3">
        <Skeleton className="h-6 w-[260px] max-w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[180px] w-full" />
      </div>
    );
  }

  const typeId = `${fieldId}-type`;
  const hazardId = `${fieldId}-hazard`;
  const usageClassId = `${fieldId}-usage-class`;
  const personExposureId = `${fieldId}-person-exposure`;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      {/* Presets — the regression anchors, visible rather than buried in a script. */}
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">{t("context.simulator.presets")}</span>
        {PRESETS.filter((preset) => preset.anchor).map((preset) => (
          <Button
            key={preset.key}
            variant="outline"
            size="sm"
            onClick={() => applyPreset(preset)}
          >
            {t(`context.simulator.preset.${preset.key}`)}
          </Button>
        ))}
        {/* Kept apart from the anchors: this one carries no expected value and
            is here to show movement, not to guard a regression. */}
        <span className="text-xs text-muted-foreground">{t("context.simulator.examples")}</span>
        {PRESETS.filter((preset) => !preset.anchor).map((preset) => (
          <Button
            key={preset.key}
            variant="outline"
            size="sm"
            onClick={() => applyPreset(preset)}
          >
            {t(`context.simulator.preset.${preset.key}`)}
          </Button>
        ))}
      </div>

      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[min(280px,100%)] flex-col gap-1.5">
          <Label htmlFor={typeId}>{t("context.simulator.type")}</Label>
          <Select
            value={typeCode ?? ""}
            onValueChange={(value: string) => {
              setTypeCode(value);
              setHazardCode(undefined);
              setExpected(null);
            }}
          >
            <SelectTrigger id={typeId}>
              <SelectValue placeholder={t("context.simulator.selectType")} />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {types.map((type: any) => (
                <SelectItem key={type.code} value={type.code}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex w-[min(280px,100%)] flex-col gap-1.5">
          <Label htmlFor={hazardId}>{t("context.simulator.hazard")}</Label>
          <Select
            value={hazardCode ?? ""}
            disabled={!typeCode}
            onValueChange={(value: string) => {
              setHazardCode(value);
              setExpected(null);
            }}
          >
            <SelectTrigger id={hazardId}>
              <SelectValue placeholder={t("context.simulator.selectHazard")} />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {hazardOptions.map((hazard: any) => (
                <SelectItem key={hazard.code} value={hazard.code}>
                  {`${hazard.label} · ${damageTypeLabel(t, hazard.damageType)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex w-[min(240px,100%)] flex-col gap-1.5">
          <Label htmlFor={usageClassId}>{t("context.usageClass")}</Label>
          <Select
            value={usageClass ?? NOT_RECORDED}
            onValueChange={(value: string) =>
              setUsageClass(value === NOT_RECORDED ? undefined : value)
            }
          >
            <SelectTrigger id={usageClassId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              <SelectItem value={NOT_RECORDED}>{t("context.notRecorded")}</SelectItem>
              {(options?.usageClasses || []).map((entry: any) => (
                <SelectItem key={entry.usageClass} value={entry.usageClass}>
                  {entry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex w-[min(240px,100%)] flex-col gap-1.5">
          <Label htmlFor={personExposureId}>{t("context.personExposure")}</Label>
          <Select
            value={personExposure ?? NOT_RECORDED}
            onValueChange={(value: string) =>
              setPersonExposure(value === NOT_RECORDED ? undefined : value)
            }
          >
            <SelectTrigger id={personExposureId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              <SelectItem value={NOT_RECORDED}>{t("context.notRecorded")}</SelectItem>
              {(options?.personExposures || []).map((value: string) => (
                <SelectItem key={value} value={value}>
                  {t(`context.personExposureValue.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* The whole context space. Clicking a cell drives the readout below, so
          the field is the navigation and the band bar the detail. */}
      {matrix ? (
        <ContextMatrix
          matrix={matrix}
          selectedUsageClass={usageClass ?? null}
          selectedPersonExposure={personExposure ?? null}
          onSelect={(nextUsageClass: string, nextPersonExposure: string) => {
            setUsageClass(nextUsageClass);
            setPersonExposure(nextPersonExposure);
            setExpected(null);
          }}
        />
      ) : null}

      {!result && !typeCode ? (
        // Fachlicher Leerzustand: Der Simulator braucht Typ und Gefährdung,
        // sonst gibt es nichts zu rechnen.
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-sm font-semibold">{t("context.simulator.emptyTitle")}</p>
          <p className="max-w-prose text-sm text-muted-foreground">
            {t("context.simulator.emptyHint")}
          </p>
        </div>
      ) : null}

      {result ? (
        <div className="flex min-w-0 flex-col gap-3 rounded-md border p-4">
          {result.siValue === null ? (
            <p className="min-w-0 break-words text-sm text-muted-foreground">
              {t("context.simulator.noBase")}
            </p>
          ) : (
            <>
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <span className="font-semibold">
                  {damageTypeLabel(t, result.hazard?.damageType)}
                </span>
                {expected !== null ? (
                  <span
                    className={`text-xs font-semibold ${
                      deviates ? "text-destructive" : "text-success"
                    }`}
                  >
                    {deviates
                      ? t("context.simulator.deviates", { expected })
                      : t("context.simulator.matchesExpected", { expected })}
                  </span>
                ) : null}
                {busy ? (
                  <Loader2
                    aria-label={t("global.loading")}
                    className="h-4 w-4 animate-spin text-muted-foreground"
                  />
                ) : null}
              </div>

              <BandBar band={result.band} kappa={result.kappa} token={resultToken} t={t} />

              <div className="flex min-w-0 flex-col gap-2 text-sm">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span>
                    {t("context.simulator.baseValue")}: <strong>{result.baseValue}</strong>
                  </span>
                  {result.derivation ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2"
                      aria-expanded={showTrace}
                      onClick={() => setShowTrace(!showTrace)}
                    >
                      {t("hazard.severity.derivation")}
                    </Button>
                  ) : null}
                </div>

                {showTrace && result.derivation ? (
                  <div className="flex min-w-0 flex-col gap-0.5 rounded-md bg-muted p-3 font-mono text-xs">
                    <div className="min-w-0 break-words">
                      {t("hazard.severity.legalDomain")}: {result.derivation.legalDomain || "—"}
                    </div>
                    <div className="min-w-0 break-words">
                      {t("hazard.severity.ceremonyScore")}: {result.derivation.ceremonyScore ?? "—"}
                    </div>
                    {(result.derivation.ceremonyBreakdown || []).map((part: any, index: number) => (
                      <div key={index} className="min-w-0 break-words pl-4">
                        {part.evidence || part.component}{" "}
                        <span className="font-semibold text-warning">
                          {part.points >= 0 ? "+" : ""}
                          {part.points}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="min-w-0 break-words">
                  {t("context.simulator.shift")}:{" "}
                  <strong>
                    {result.kappa > 0 ? "+" : ""}
                    {result.kappa}
                  </strong>{" "}
                  {result.kappa === 0
                    ? t("context.simulator.noShift")
                    : result.kappa > 0
                      ? t("context.simulator.oneBandUp")
                      : t("context.simulator.oneBandDown")}
                </div>

                <div className="mt-2 flex min-w-0 flex-col gap-2">
                  {(result.firedRules || []).map((rule: any) => (
                    <div key={rule.code} className="min-w-0">
                      <strong>{rule.code}</strong> {rule.label}{" "}
                      <span className="font-semibold text-success">
                        {rule.delta > 0 ? "+" : ""}
                        {rule.delta}
                      </span>
                      <div className="min-w-0 break-words pl-4 text-muted-foreground">
                        {rule.rationale}
                      </div>
                    </div>
                  ))}

                  {/* Rules that did not fire, with the reason. At review time the
                      more important question is often why something did *not*
                      happen. "Not evaluable" is kept apart from "did not match" —
                      the same difference as grey versus green. */}
                  {(result.notFiredRules || []).map((rule: any) => (
                    <div
                      key={rule.code}
                      className={`min-w-0 break-words ${
                        rule.notEvaluable ? "text-warning" : "text-muted-foreground"
                      }`}
                    >
                      <strong>{rule.code}</strong>{" "}
                      {rule.notEvaluable
                        ? t(`context.simulator.notEvaluable.${rule.reason}`)
                        : t(`context.simulator.notFired.${rule.reason}`)}
                    </div>
                  ))}
                </div>

                <div className="mt-2 min-w-0 break-words text-base">
                  {t("context.simulator.resultValue")}: <strong>{result.siValue}</strong>
                  {result.siClass ? ` · ${t("context.simulator.class")} ${result.siClass}` : ""}
                </div>

                {result.provisional ? (
                  <p className="min-w-0 break-words text-xs text-warning">
                    {t("context.simulator.provisionalHint")}
                  </p>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* Sweep — a product finding, not a test artefact. */}
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap">
          <Button
            variant="outline"
            className="gap-2 max-sm:w-full"
            disabled={busy || sweepBusy}
            onClick={runSweep}
          >
            {sweepBusy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
            {t("context.sweep.run")}
          </Button>
        </div>
        {sweep ? (
          <div className="flex min-w-0 flex-col gap-2 text-sm">
            <div className="min-w-0 break-words">
              {t("context.sweep.combinations", { count: sweep.combinations })} ·{" "}
              {t("context.sweep.shifted", {
                count: sweep.shifted,
                percent: Math.round((sweep.shiftedShare || 0) * 100),
              })}
            </div>
            <div className="min-w-0 break-words">
              {t("context.sweep.exposureIneffective", {
                count: sweep.exposureIneffectiveHazards?.length || 0,
                total: sweep.hazardCount,
                percent: Math.round((sweep.exposureIneffectiveShare || 0) * 100),
              })}
              {sweep.exposureIneffectiveHazards?.length ? (
                <span className="text-muted-foreground">
                  {" "}
                  ({sweep.exposureIneffectiveHazards.join(", ")})
                </span>
              ) : null}
            </div>
            {sweep.rulesNeverFiring?.length ? (
              <p className="min-w-0 break-words text-destructive">
                {t("context.sweep.rulesNeverFiring", {
                  rules: sweep.rulesNeverFiring.map((rule: any) => rule.code).join(", "),
                })}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
